import { supabase } from './supabase'
import { AuditLogEntry } from '../hooks/useAuditLog'

/**
 * Middleware de auditoria que intercepta operações do Supabase
 * para registrar automaticamente logs de acesso conforme LGPD
 */
class AuditMiddleware {
  private static instance: AuditMiddleware
  private isEnabled = true

  private constructor() {}

  static getInstance(): AuditMiddleware {
    if (!AuditMiddleware.instance) {
      AuditMiddleware.instance = new AuditMiddleware()
    }
    return AuditMiddleware.instance
  }

  /**
   * Habilita ou desabilita o middleware de auditoria
   */
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled
    console.log(`🔍 [AUDIT MIDDLEWARE] ${enabled ? 'Habilitado' : 'Desabilitado'}`)
  }

  /**
   * Mapeia tabelas para tipos de recursos
   */
  private getResourceType(table: string): AuditLogEntry['resource_type'] {
    const mapping: Record<string, AuditLogEntry['resource_type']> = {
      'medical_records': 'medical_record',
      'medical_documents': 'document',
      'user_profiles': 'user_profile',
      'access_permissions': 'access_control',
      'audit_logs': 'audit_log'
    }
    return mapping[table] || 'medical_record'
  }

  /**
   * Mapeia operações do Supabase para tipos de ação
   */
  private getActionType(operation: string): AuditLogEntry['action_type'] {
    const mapping: Record<string, AuditLogEntry['action_type']> = {
      'select': 'view',
      'insert': 'create',
      'update': 'update',
      'delete': 'delete',
      'upsert': 'update'
    }
    return mapping[operation] || 'view'
  }

  /**
   * Registra log de auditoria para operação
   */
  private async logOperation(
    operation: string,
    table: string,
    data?: any,
    filters?: any,
    result?: any
  ) {
    if (!this.isEnabled) return

    try {
      // Obter usuário atual
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return

      const resourceType = this.getResourceType(table)
      const actionType = this.getActionType(operation)

      // Extrair ID do recurso se disponível
      let resourceId: string | undefined
      if (result?.data && Array.isArray(result.data) && result.data.length > 0) {
        resourceId = result.data[0].id
      } else if (result?.data && !Array.isArray(result.data)) {
        resourceId = result.data.id
      } else if (data?.id) {
        resourceId = data.id
      } else if (filters?.eq && filters.eq[0] === 'id') {
        resourceId = filters.eq[1]
      }

      // Preparar dados acessados (anonimizados)
      const accessedData = this.anonymizeData({
        operation,
        table,
        filters: filters || {},
        data_keys: data ? Object.keys(data) : [],
        result_count: Array.isArray(result?.data) ? result.data.length : (result?.data ? 1 : 0)
      })

      // Registrar log diretamente no banco (evitar recursão)
      await this.insertAuditLogDirect({
        user_id: user.id,
        user_email: user.email,
        action_type: actionType,
        resource_type: resourceType,
        resource_id: resourceId,
        resource_description: `${operation.toUpperCase()} em ${table}`,
        accessed_data: accessedData,
        user_agent: navigator.userAgent,
        session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        metadata: {
          operation,
          table,
          timestamp_client: new Date().toISOString()
        }
      })

    } catch (error) {
      console.error('❌ [AUDIT MIDDLEWARE] Erro ao registrar log:', error)
    }
  }

  /**
   * Insere log de auditoria diretamente (sem middleware para evitar recursão)
   */
  private async insertAuditLogDirect(logData: any) {
    // Desabilitar temporariamente o middleware para evitar recursão
    const wasEnabled = this.isEnabled
    this.isEnabled = false

    try {
      const { error } = await supabase
        .from('audit_logs')
        .insert([logData])

      if (error) {
        console.error('❌ [AUDIT] Erro ao inserir log direto:', error)
      }
    } finally {
      this.isEnabled = wasEnabled
    }
  }

  /**
   * Anonimiza dados sensíveis
   */
  private anonymizeData(data: Record<string, any>): Record<string, any> {
    const sensitiveFields = ['cpf', 'rg', 'phone', 'address', 'email', 'date_of_birth', 'password']
    const anonymized = { ...data }

    const anonymizeValue = (value: any): any => {
      if (typeof value === 'string' && value.length > 4) {
        return value.substring(0, 2) + '*'.repeat(value.length - 4) + value.substring(value.length - 2)
      } else if (typeof value === 'string') {
        return '*'.repeat(value.length)
      } else if (typeof value === 'object' && value !== null) {
        return this.anonymizeData(value)
      }
      return '[ANONIMIZADO]'
    }

    Object.keys(anonymized).forEach(key => {
      if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        anonymized[key] = anonymizeValue(anonymized[key])
      }
    })

    return anonymized
  }

  /**
   * Intercepta operações do Supabase
   */
  interceptSupabaseOperations() {
    if (typeof window === 'undefined') return // Não executar no servidor

    // Interceptar métodos do PostgrestQueryBuilder
    const originalFrom = supabase.from.bind(supabase)
    
    supabase.from = (table: string) => {
      const queryBuilder = originalFrom(table)
      
      // Interceptar operações de SELECT
      const originalSelect = queryBuilder.select.bind(queryBuilder)
      queryBuilder.select = (...args: any[]) => {
        const result = originalSelect(...args)
        
        // Interceptar execução da query
        const originalThen = result.then.bind(result)
        result.then = (onFulfilled?: any, onRejected?: any) => {
          return originalThen(
            (data: any) => {
              this.logOperation('select', table, undefined, args[0], data)
              return onFulfilled ? onFulfilled(data) : data
            },
            onRejected
          )
        }
        
        return result
      }

      // Interceptar operações de INSERT
      const originalInsert = queryBuilder.insert.bind(queryBuilder)
      queryBuilder.insert = (data: any) => {
        const result = originalInsert(data)
        
        const originalThen = result.then.bind(result)
        result.then = (onFulfilled?: any, onRejected?: any) => {
          return originalThen(
            (response: any) => {
              this.logOperation('insert', table, data, undefined, response)
              return onFulfilled ? onFulfilled(response) : response
            },
            onRejected
          )
        }
        
        return result
      }

      // Interceptar operações de UPDATE
      const originalUpdate = queryBuilder.update.bind(queryBuilder)
      queryBuilder.update = (data: any) => {
        const result = originalUpdate(data)
        
        const originalThen = result.then.bind(result)
        result.then = (onFulfilled?: any, onRejected?: any) => {
          return originalThen(
            (response: any) => {
              this.logOperation('update', table, data, undefined, response)
              return onFulfilled ? onFulfilled(response) : response
            },
            onRejected
          )
        }
        
        return result
      }

      // Interceptar operações de DELETE
      const originalDelete = queryBuilder.delete.bind(queryBuilder)
      queryBuilder.delete = () => {
        const result = originalDelete()
        
        const originalThen = result.then.bind(result)
        result.then = (onFulfilled?: any, onRejected?: any) => {
          return originalThen(
            (response: any) => {
              this.logOperation('delete', table, undefined, undefined, response)
              return onFulfilled ? onFulfilled(response) : response
            },
            onRejected
          )
        }
        
        return result
      }

      return queryBuilder
    }

    console.log('🔍 [AUDIT MIDDLEWARE] Interceptação do Supabase configurada')
  }
}

// Exportar instância singleton
export const auditMiddleware = AuditMiddleware.getInstance()

// Inicializar interceptação automaticamente
if (typeof window !== 'undefined') {
  auditMiddleware.interceptSupabaseOperations()
}

export default auditMiddleware